<template>
  <div :class="$.create_column_view">
    <div :class="$.create_column_view__body">
      <div :class="[$.slot, $.slot__name]">
        <vgp-label-slot :label="locale('columns_bar_new_column_input_label')">
          <template #body>
            <vgp-input
              ref="nameInput"
              v-model="name"
              :value="name"
              :small="true"
              :placeholder="column.name"
              @onChange="onColumnNameChange"
              @input="onInput"
            />
          </template>
        </vgp-label-slot>
      </div>
      <div
        v-if="showMatchesMessage && !closedMessageByUser"
        :class="$.message"
      >
        <div :class="$.wrapper">
          <div :class="$.info">
            <svg-sprite
              type="bold"
              name="info"
              :size="20"
              color="#1565C0"
            />
            <div :class="$.text">
              {{ locale('columns_bar_message_if_name_already_exist') }}
            </div>
            <div
              :class="$.close"
              @click="closeMessage"
            >
              <svg-sprite
                name="close-1"
                :size="16"
              />
            </div>
          </div>
          <div :class="$.button">
            <vgp-button
              id="gp_autotest_columns_bar_message_open_existed_columns_tab"
              :label="locale('columns_bar_message_open_existed_columns_tab')"
              type="primary"
              :x-small="true"
              @onClick="showExistingColumnsByUserValue"
            />
          </div>
        </div>
      </div>
      <div :class="[$.slot, $.slot__custom_column]">
        <vgp-label-slot :label="locale('columns_bar_new_column_types_select_label')">
          <template #body>
            <dropdown-select
              :drop-list="columnTypes"
              :multiselect="false"
              :show-selected-first="false"
              :show-reset-btn="false"
              :highlight-active-state="false"
              item-template="withIcon"
              track-by-item-tooltip="tooltip"
              track-by-text="name"
              track-by-icon-name="icon"
              :popup-max-height="305"
              :selected="columnTypes.find(i => i.id === column.id)"
              @selected="onSelectColumnType"
            />
          </template>
        </vgp-label-slot>
      </div>
      <div
        v-if="columnType === 'select'"
        ref="options"
        :class="[$.slot, $.slot__options, $.slot__option_select]"
      >
        <vgp-label-slot :label="locale('columns_bar_select_options_label')">
          <template #body>
            <select-options
              :options="userOptions"
              :max-height="optionsListMaxHeight"
              :error="errorValidation"
              @resetError="resetError"
              @onChange="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>

      <div
        v-if="columnType === 'number'"
        :class="[$.slot, $.slot__option_number]"
      >
        <vgp-label-slot :label="locale('columns_bar_aggregations_options_label')">
          <template #body>
            <dropdown-select
              :drop-list="optionsAggregations"
              :multiselect="false"
              :show-selected-first="false"
              :show-reset-btn="false"
              :highlight-active-state="false"
              item-template="text"
              track-by-text="value"
              :popup-max-height="100"
              :selected="userOptions"
              @selected="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>

      <div
        v-if="columnType === 'colors'"
        ref="options"
        :class="[$.slot, $.slot__options, $.slot__option_colors]"
      >
        <vgp-label-slot :label="locale('columns_bar_colors_options_label')">
          <template #body>
            <color-options
              :options="userOptions"
              :max-height="optionsListMaxHeight"
              @onChange="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>

      <div
        v-if="columnType === 'multiselect'"
        ref="options"
        :class="[$.slot, $.slot__options, $.slot__option_multiselect]"
      >
        <vgp-label-slot :label="locale('columns_bar_multiselect_options_label')">
          <template #body>
            <multiselect-options
              :options="userOptions"
              :max-height="optionsListMaxHeight"
              :error="errorValidation"
              @resetError="resetError"
              @onChange="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>

      <div
        v-if="columnType === 'resources'"
        :class="[$.slot, $.slot__option_resources]"
      >
        <vgp-label-slot :label="locale('columns_bar_resources_options_label')">
          <template #body>
            <dropdown-select
              :drop-list="groupedResources"
              :placeholder="locale('columns_bar_resources_select_placeholder')"
              :multiselect="true"
              :show-selected-first="false"
              :show-reset-btn="false"
              :highlight-active-state="false"
              :devider-color="'#999999'"
              :use-search="true"
              item-template="user"
              track-by-text="name"
              group-by="group"
              :use-select-all="true"
              :popup-max-height="200"
              :selected="userOptions"
              @selected="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>

      <div
        v-if="columnType === 'tags'"
        ref="options"
        :class="[$.slot, $.slot__options, $.slot__option_tags]"
      >
        <vgp-label-slot :label="locale('columns_bar_tags_options_label')">
          <template #body>
            <tag-options
              :options="userOptions"
              :max-height="optionsListMaxHeight"
              :error="errorValidation"
              @resetError="resetError"
              @onChange="changeOptions"
            />
          </template>
        </vgp-label-slot>
      </div>
    </div>
    <div
      ref="footer"
      :class="$.create_column_view__footer"
    >
      <vgp-button
        id="gp_autotest_columns_bar_save_new_column"
        :label="locale('common_save')"
        :disabled="disabledSavingButton"
        type="primary"
        @onClick="onSaveClick"
      />
    </div>
  </div>
</template>

<script>
import projectModel from '../../../../../models/projects';
import DropdownSelect from '../../../../common/VueComponents/dropdownSelect/dropdownSelect.vue';
import vgpLabelSlot from '../../../../common/VueComponents/label/vgpLabelSlot.vue';
import vgpInput from '../../../../common/VueComponents/vInput/vgpInput.vue';
import constants from '../../constants';
import colorOptions from '../dropdownOptions/colors.vue';
import tagOptions from '../dropdownOptions/tags.vue';
import selectOptions from '../dropdownOptions/selectOptions.vue';
import multiselectOptions from '../dropdownOptions/multiselectOptions.vue';
import vgpButton from '../../../../common/VueComponents/globalButton/vgpButton.vue';
import svgSprite from '../../../../common/VueComponents/VueIcon/svgSprite.vue';
import iconButton from '../../../../common/VueComponents/globalButton/withIcon/iconButton.vue';

export default {
  name: 'ColumnsBarCreateColumnTab',
  components: {
    vgpInput, DropdownSelect, vgpLabelSlot, colorOptions, tagOptions, selectOptions, multiselectOptions, vgpButton, svgSprite, iconButton,
  },
  props: {
    allColumns: { type: Array, required: true, default: () => [] },
    projectColumnsIds: { type: Array, required: false, default: () => [] },
    newColumn: { type: Object, required: false, default: () => {} },
  },
  emits: {
    showExistingColumnsByUserValue: null,
    showMarkerOnExistingTab: null,
    setNewColumnData: Object,
    saveNewColumn: null,
  },
  data() {
    return {
      locale: __,
      name: '',
      columnType: null,
      column: null,
      optionsListMaxHeight: 500,
      columnTypes: constants.CUSTOM_COLUMNS,
      optionsAggregations: constants.OPTIONS_AGGREGATIONS_SELECT,
      userOptions: [],
      showMatchesMessage: false,
      closedMessageByUser: false,
      errorValidation: false,
      disabledSavingButton: false,
    };
  },
  computed: {
    projectResources() {
      const projectId = projectModel.getActiveGanttId();

      return this.$store.getters['resourcesModel/getResourcesByProjectId'](projectId);
    },
    groupedResources() {
      const groupedResources = this.projectResources.map((item, index) => {
        const userPic = `${GT.cdn}/imgs/default/default-ava.svg`;
        const resource = {};

        resource.id = item.id;
        resource.resource_id = item.id;
        resource.img = item.photo || userPic;
        resource.name = item.name;
        resource.is_default = 0;
        resource.sort_order = index;

        if (item.userId) {
          resource.group = __('resources_layout_header_title_1');
        } else {
          resource.group = __('resources_layout_header_title_2');
        }

        return resource;
      });

      return groupedResources;
    },
    changedOptions() {
      if (['select', 'multiselect', 'tags'].includes(this.columnType)) {
        const options = this.userOptions.filter(opt => opt.value !== '');

        return options;
      }

      return this.userOptions;
    },
  },
  watch: {
    changedOptions(options) {
      this.$emit('setNewColumnData', { options });
    },
    name(name) {
      this.$emit('setNewColumnData', { name });
    },
    column(column) {
      this.$emit('setNewColumnData', { type: column.id, defaultName: column.name });
    },
    groupedResources(resources) {
      if (this.columnType === 'resources') {
        this.userOptions = this.userOptions.filter(opt => resources.some(res => res.id === opt.id));
      }
    },
  },
  mounted() {
    this.calcOptionsListMaxHeight();

    window.addEventListener('resize', this.calcOptionsListMaxHeight);
    setTimeout(() => {
      const nameInput = this.$refs.nameInput;

      if (nameInput) {
        nameInput.focusElement();
      }
    }, 0);
  },
  beforeDestroy() {
    this.columnType = null;
    this.errorValidation = false;

    window.removeEventListener('resize', this.calcOptionsListMaxHeight);
  },
  created() {
    const { name, type, options } = this.newColumn;

    if (name && name.length) {
      this.name = name;
    }

    if (type) {
      this.column = constants.CUSTOM_COLUMNS.find(col => col.id === type);
      this.getColumnTypeByColumnId(this.column.id);
    } else {
      this.column = constants.CUSTOM_COLUMNS[0];
    }

    if (options && options.length) {
      this.userOptions = options;
    }
  },
  methods: {
    onSelectColumnType(selectedData) {
      const selected = selectedData[0];

      this.column = selected;
      this.userOptions = [];
      this.getColumnTypeByColumnId(selected.id);
    },
    getColumnTypeByColumnId(id) {
      switch (id) {
      case 1:
        this.columnType = 'select';
        setTimeout(() => {
          this.calcOptionsListMaxHeight();
        }, 0);
        break;
      case 3:
        this.columnType = 'number';
        this.userOptions = [this.optionsAggregations[0]];
        break;
      case 6:
        this.columnType = 'colors';
        setTimeout(() => {
          this.calcOptionsListMaxHeight();
        }, 0);
        break;
      case 7:
        this.columnType = 'multiselect';
        setTimeout(() => {
          this.calcOptionsListMaxHeight();
        }, 0);
        break;
      case 8:
        this.columnType = 'resources';
        break;
      case 9:
        this.columnType = 'tags';
        setTimeout(() => {
          this.calcOptionsListMaxHeight();
        }, 0);
        break;
      default:
        this.columnType = null;
      }
    },
    onColumnNameChange(event) {
      const value = event.target.value.trim();

      this.name = value;
    },
    checkMatches(value) {
      const existingColumns = this.allColumns.filter(col => !this.projectColumnsIds.includes(col.id));
      const result = existingColumns.find(col => col.name.toLowerCase().startsWith(this.name.trim().toLowerCase()) && col.name.length === value.length);

      if (value.length && result && this.name.length) {
        this.showMatchesMessage = true;
        this.$emit('showMarkerOnExistingTab', true);
      } else {
        this.showMatchesMessage = false;
        this.$emit('showMarkerOnExistingTab', false);
      }
    },
    changeOptions(options) {
      this.userOptions = options;
      this.errorValidation = false;
    },
    onSaveClick() {
      if (['select', 'multiselect', 'tags'].includes(this.columnType) && !this.changedOptions.length) {
        this.errorValidation = true;

        return;
      }

      this.disabledSavingButton = true;
      this.$emit('saveNewColumn');
      userExtAnalytics.log('columns_sidebar_save_new_column_click');
    },
    closeMessage() {
      this.showMatchesMessage = false;
      this.closedMessageByUser = true;
    },
    showExistingColumnsByUserValue() {
      this.$emit('showExistingColumnsByUserValue');
      userExtAnalytics.log('columns_sidebar_show_existing_columns_by_user_value');
    },
    onInput(value) {
      if (value.trim().length === 0) {
        this.closedMessageByUser = false;
        this.showMatchesMessage = false;
        this.$emit('showMarkerOnExistingTab', false);
      }

      this.name = value.trim();

      const debouncedCheck = _.debounce(this.checkMatches.bind(this), 1000);

      debouncedCheck(value);
    },
    resetError() {
      this.errorValidation = false;
    },
    calcOptionsListMaxHeight() {
      const options = this.$refs.options;

      if (options) {
        const columnsBar = this.$parent.$refs.columnsBar;
        const barFooter = this.$refs.footer;

        const optionsCoords = options.getBoundingClientRect();
        const columnsBarCoords = columnsBar.getBoundingClientRect();
        const topDiff = optionsCoords.top - columnsBarCoords.top;

        const optionsListMaxHeight = columnsBar.clientHeight - topDiff - barFooter.clientHeight - 74;

        this.optionsListMaxHeight = optionsListMaxHeight;
      }
    },
  },
};
</script>

<style module='$' src='./createColumnTab.less' lang='less'></style>
